import { GoalIndicationParameters } from '../contexts/investment-robot/GoalIndicationContext';
import { DeepPartial } from '../utils/typings';
import { i18n, translationResources } from '../locales';

const BASE_PATH_GOAL_INDICATION = '/goal-indication';

export type QuestionType =
    | 'money'
    | 'date'
    | 'age'
    | 'quantity'
    | 'switch'
    | 'select'
    | 'riskProfile'
    | 'pensionPeriodicIncome'
    | 'pensionProposedCapitalGoal'
    | 'contactDetails';

export type Goal = {
    id: GoalIndicationParameters['selectedGoal'];
    label: string;
    pages: GoalPage[];
};

export type TooltipInput = {
    tooltipId: string,
    tooltipTitle: string,
    tooltipText: string,
}

export type GoalPage = {
    pageId: string;
    question: {
        label: (parameters?: GoalIndicationParameters) => string;
        description?: (parameters?: GoalIndicationParameters) => string;
        tooltip?: TooltipInput;
        type: QuestionType;
        select?: { options: { label: string; value: string }[]; placeholder?: string };
        riskProfile?: {
            descriptions: { id: string; title: string; description: string }[];
        };
    };
    value: {
        onChange: (value: string) => DeepPartial<GoalIndicationParameters>;
        validate: (value: string) => string | null; // String == error message
        rules?: {
            min?: number;
            minFn?: (parameters: GoalIndicationParameters) => number;
            max?: number;
            maxFn?: (parameters: GoalIndicationParameters) => number;
            step?: number;
        };
        get: (parameters: GoalIndicationParameters) => string | undefined;
        sliderPreviewText?: (quantity: string) => string;
        placeholder?: string;
        defaultValueOnEmpty?: (parameters: GoalIndicationParameters) => string;
    };
    routes: {
        current: string;
        next: string;
    };
};

type InvestmentTranslation = typeof translationResources['nl']['investment'];
type InvestmentTranslationKey = keyof InvestmentTranslation;

const t = (key: InvestmentTranslationKey) => {
    return i18n.t(key, {
        ns: 'investment',
    });
};

export const GENERAL_PAGES: GoalPage[] = [
    {
        pageId: 'general-expert-level',
        question: {
            label: (value) => t('goal-indication-screen-general-expert-level-title'), //'Hoe bekend ben je met beleggen?',
            type: 'select',
            select: {
                options: [
                    { label: t('goal-indication-screen-general-expert-level-1-title'), value: '1' },
                    { label: t('goal-indication-screen-general-expert-level-2-title'), value: '2' },
                    { label: t('goal-indication-screen-general-expert-level-3-title'), value: '3' },
                    { label: t('goal-indication-screen-general-expert-level-4-title'), value: '4' },
                ],
                placeholder: 'Selecteer hier uw ervaring',
            },
        },
        value: {
            onChange: (level) => {
                if (level === 'none') return {};
                const _level = +level;
                if (_level !== 1 && _level !== 2 && _level !== 3 && _level !== 4) return { selectedExpertLevel: 1 };
                return {
                    selectedExpertLevel: _level,
                };
            },
            defaultValueOnEmpty: undefined,
            validate: (value) => {
                if (value === '' || value === 'none') return 'Selecteer alstublieft uw ervaring.';
                return null;
            },
            get: (params) => {
                return params.selectedExpertLevel ? params.selectedExpertLevel + '' : undefined;
            },
        },
        routes: {
            current: BASE_PATH_GOAL_INDICATION + '/general/expert-level',
            next: BASE_PATH_GOAL_INDICATION + '/general/risk-reduction',
        },
    },
    {
        pageId: 'general-risk-reduction',
        question: {
            label: (value) => t('goal-indication-screen-general-risk-reduction-title'), //'Wil je risicoafbouw meenemen in de berekening?',
            description: () => t('goal-indication-screen-general-risk-reduction-description'), //'Risicoafbouw verlaagt fluctuatie aan het einde. Schuif met de knop voor resultaat. Wil je risicoafbouw inschakelen?',
            type: 'switch',
        },
        value: {
            onChange: (boolean) => {
                const isTrue = boolean === 'true' ? true : false;
                return {
                    risk: {
                        reduction: {
                            isEnabled: isTrue,
                        },
                    },
                };
            },
            validate: () => null,
            get: (params) => (params.selectedExpertLevel || 1) + '',
        },
        routes: {
            current: BASE_PATH_GOAL_INDICATION + '/general/risk-reduction',
            next: BASE_PATH_GOAL_INDICATION + '/general/risk-profile',
        },
    },
    {
        pageId: 'general-risk-profile',
        question: {
            label: (value) => t('goal-indication-screen-general-risk-profile-title'),
            description: () => t('goal-indication-screen-general-risk-profile-description'), //'Kies hieronder een strategie en zie wat er met de waardes gebeurt. Meer risico betekent meer kans op grotere winst of verlies.',
            type: 'riskProfile',
            riskProfile: {
                descriptions: [
                    {
                        id: '8',
                        title: 'Zeer defensief betekent',
                        description:
                            'Je verwachte bedrag zal varieëren van € 32.400 tot € 41.700. Bedenkt wel dat een keuze voor neutraal over een 10 jaar een fluctuatie heeft van -6 tot 6%. Dus schrik niet als je soms in de min komt.',
                    },
                    {
                        id: '9',
                        title: 'Defensief betekent',
                        description:
                            'Je verwachte bedrag zal varieëren van € 32.400 tot € 41.700. Bedenkt wel dat een keuze voor neutraal over een 10 jaar een fluctuatie heeft van -6 tot 6%. Dus schrik niet als je soms in de min komt.',
                    },
                    {
                        id: '10',
                        title: 'Neutraal betekent',
                        description:
                            'Je verwachte bedrag zal varieëren van € 32.400 tot € 41.700. Bedenkt wel dat een keuze voor neutraal over een 10 jaar een fluctuatie heeft van -6 tot 6%. Dus schrik niet als je soms in de min komt.',
                    },
                    {
                        id: '11',
                        title: 'Optimistisch betekent',
                        description:
                            'Bij een offensieve tactiek kan je meer winst behalen, maar bij een slechte markt ook meer verlies. Bedenkt dat een keuze voor offensief over een 10 jaar een fluctuatie heeft van -6 tot 6%. Dus schrik niet als je soms in de min komt.',
                    },
                    {
                        id: '12',
                        title: 'Zeer optimistisch betekent',
                        description:
                            'Je verwachte bedrag zal varieëren van € 32.400 tot € 41.700. Bedenkt wel dat een keuze voor neutraal over een 10 jaar een fluctuatie heeft van -6 tot 6%. Dus schrik niet als je soms in de min komt.',
                    },
                ],
            },
        },
        value: {
            onChange: (id) => {
                return {
                    risk: {
                        selectedId: id,
                    },
                };
            },
            validate: () => null,
            get: (params) => params.risk?.selectedId,
        },
        routes: {
            current: BASE_PATH_GOAL_INDICATION + '/general/risk-profile',
            next: '/forecast',
        },
    },
];

const HOUSE_GOAL: Goal = {
    id: 'house',
    label: t('goal-indication-screen-house-title'),
    pages: [
        {
            pageId: 'house-capital-goal-amount',
            question: {
                label: (value) => t('goal-indication-screen-house-capital-goal-title'), //'Wat heb je nodig voor een huis?',
                description: () => t('goal-indication-screen-house-capital-goal-description'), //'Dit bedrag vormt je financieel doel. Aan de hand van dit bedrag wordt bepaald of je met je investering je doel bereikt. Het is mogelijk om dit later aan te passen.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/house/capital-goal-amount',
                next: BASE_PATH_GOAL_INDICATION + '/house/capital-goal-date',
            },
            value: {
                placeholder: '€100.000',
                defaultValueOnEmpty: () => '10000000',
                validate: (cents) => {
                    // if (+cents > 3000) {
                    //     return 'Mag niet hoger';
                    // }
                    return null;
                },
                onChange: (value) => {
                    return {
                        goals: {
                            house: {
                                capitalGoal: {
                                    amount: +value,
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.house?.capitalGoal?.amount !== undefined
                        ? params.goals.house.capitalGoal.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'house-capital-goal-date',
            question: {
                label: (value) => t('goal-indication-screen-general-capital-goal-inyears-title'), //'Over hoeveel jaar heeft u het geld nodig?',
                //description: t('goal-indication-screen-general-capital-goal-inyears-description'), //'Dit is het aantal jaren dat u verwacht te gaan beleggen.',
                type: 'quantity',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/house/capital-goal-date',
                next: BASE_PATH_GOAL_INDICATION + '/house/initial-deposit',
            },
            value: {
                placeholder: '€5.000',
                defaultValueOnEmpty: () => '500000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            house: {
                                capitalGoal: {
                                    inYears: +value,
                                },
                            },
                        },
                    };
                },
                rules: {
                    min: 1,
                    max: 50,
                    step: 1,
                },
                get: (params) => {
                    return params.goals?.house?.capitalGoal?.inYears
                        ? params.goals?.house?.capitalGoal?.inYears + ''
                        : undefined;
                },
                sliderPreviewText: (years) => `Over ${years} jaar`,
            },
        },
        {
            pageId: 'house-initial-deposit',
            question: {
                label: (value) => t('goal-indication-screen-house-initial-deposit-title'), //'Wat wil je eenmalig inleggen?',
                description: () => t('goal-indication-screen-general-initial-deposit-description'), //'Je eenmalige inleg vormt de basis van je investering. Hoe meer je inlegt, hoe meer kans je hebt op een hoog resultaat.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/house/initial-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/house/periodic-deposit',
            },
            value: {
                placeholder: '€20.000',
                defaultValueOnEmpty: () => '2000000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            house: {
                                deposit: {
                                    initial: {
                                        amount: +value,
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.house?.deposit?.initial?.amount !== undefined
                        ? params.goals?.house?.deposit?.initial?.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'house-periodic-deposit',
            question: {
                label: (value) => t('goal-indication-screen-general-periodic-deposit-title'), //'Hoeveel wil je maandelijks inleggen?',
                description: () => t('goal-indication-screen-general-periodic-deposit-description'), //'Als je periodiek een bedrag inlegt, stijgt je rendement. Hoe meer je inlegt, hoe beter.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/house/periodic-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/general/expert-level',
            },
            value: {
                placeholder: '€300',
                defaultValueOnEmpty: () => '30000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            house: {
                                deposit: {
                                    periodic: {
                                        amount: +value,
                                        interval: 'monthly',
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.house?.deposit?.periodic?.amount !== undefined
                        ? params.goals?.house?.deposit?.periodic?.amount + ''
                        : undefined;
                },
            },
        },
    ],
};

const PENSION_GOAL: Goal = {
    id: 'pension',
    label: t('goal-indication-screen-pension-title'), //'Eerder met pensioen',
    pages: [
        {
            pageId: 'pension-current-age',
            question: {
                label: (value) => t('goal-indication-screen-pension-age-title'), //'Hoe oud ben je nu?',
                type: 'quantity',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/pension/current-age',
                next: BASE_PATH_GOAL_INDICATION + '/pension/retirement-age',
            },
            value: {
                validate: (cents) => {
                    return null;
                },
                onChange: (value) => {
                    return {
                        goals: {
                            pension: {
                                capitalGoal: {
                                    currentAgeInYears: +value,
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.pension?.capitalGoal?.currentAgeInYears
                        ? params.goals?.pension?.capitalGoal?.currentAgeInYears + ''
                        : undefined;
                },
                rules: {
                    min: 18,
                    max: 100,
                    step: 1,
                },
                sliderPreviewText: (years) => `Ik ben ${years} jaar oud`,
            },
        },
        {
            pageId: 'pension-retirement-age',
            question: {
                label: (value) => t('goal-indication-screen-pension-retire-age-title'), //'Op welke leeftijd wil je met pensioen?',
                type: 'quantity',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/pension/retirement-age',
                next: BASE_PATH_GOAL_INDICATION + '/pension/periodic-income',
            },
            value: {
                validate: (cents) => {
                    return null;
                },
                onChange: (value) => {
                    return {
                        goals: {
                            pension: {
                                capitalGoal: {
                                    retirememtAgeInYears: +value,
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.pension?.capitalGoal?.retirememtAgeInYears
                        ? params.goals?.pension?.capitalGoal?.retirememtAgeInYears + ''
                        : undefined;
                },
                rules: {
                    minFn: (params) => {
                        return (params.goals?.pension?.capitalGoal?.currentAgeInYears || 18) + 1;
                    },
                    max: 100,
                    step: 1,
                },
                sliderPreviewText: (years) => `Als ik ${years} jaar oud ben`,
            },
        },
        {
            pageId: 'pension-periodic-income',
            question: {
                label: (value) => t('goal-indication-screen-pension-financial-goal-title'), //'Hoeveel pensioenaanvulling wil je opbouwen?',
                description: () => t('goal-indication-screen-pension-financial-goal-description'), //'Aan de hand van de onderstaande gegevens berekenen we het bedrag dat nodig is om je pensioen aan te vullen.',
                type: 'pensionPeriodicIncome',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/pension/periodic-income',
                next: BASE_PATH_GOAL_INDICATION + '/pension/proposed-capital-goal',
            },
            value: {
                placeholder: '€100.000',
                defaultValueOnEmpty: () => '10000000',
                validate: () => {
                    return null;
                },
                onChange: () => {
                    return {};
                },
                get: () => {
                    return '';
                },
            },
        },
        {
            pageId: 'pension-proposed-capital-goal',
            question: {
                label: (value) => t('goal-indication-screen-pension-financial-goal-title-calculated'), // Hoeveel pensioenaanvulling wil je opbouwen?
                type: 'pensionProposedCapitalGoal',
                tooltip: {
                    tooltipId: "pension-proposed-capital-goal-tool-tip",
                    tooltipTitle: "",
                    tooltipText: t('goal-indication-screen-pension-financial-goal-tooltip-calculated'),
                }
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/pension/proposed-capital-goal',
                next: BASE_PATH_GOAL_INDICATION + '/pension/initial-deposit',
            },
            value: {
                placeholder: '€1.500',
                defaultValueOnEmpty: () => '150000',
                validate: () => {
                    return null;
                },
                onChange: () => {
                    return {};
                },
                get: () => {
                    return '';
                },
            },
        },
        {
            pageId: 'pension-initial-deposit',
            question: {
                label: (value) => t('goal-indication-screen-pension-initial-deposit-title'), //'Wat wil je eenmalig inleggen?',
                description: () => t('goal-indication-screen-general-initial-deposit-description'), //'Je eenmalige inleg vormt de basis van je investering. Hoe meer je inlegt, hoe meer kans je hebt op een hoog resultaat.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/pension/initial-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/pension/periodic-deposit',
            },
            value: {
                placeholder: '€10.000',
                defaultValueOnEmpty: () => '1000000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            pension: {
                                deposit: {
                                    initial: {
                                        amount: +value,
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.pension?.deposit?.initial?.amount !== undefined
                        ? params.goals?.pension?.deposit?.initial?.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'pension-periodic-deposit',
            question: {
                label: (value) => t('goal-indication-screen-general-periodic-deposit-title'), //'Hoeveel wil je maandelijks inleggen?',
                description: () => t('goal-indication-screen-general-periodic-deposit-description'), //'Als je periodiek een bedrag inlegt, stijgt je rendement. Hoe meer je inlegt, hoe beter.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/pension/periodic-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/general/expert-level',
            },
            value: {
                placeholder: '€400',
                defaultValueOnEmpty: () => '40000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            pension: {
                                deposit: {
                                    periodic: {
                                        amount: +value,
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.pension?.deposit?.periodic?.amount !== undefined
                        ? params.goals?.pension?.deposit?.periodic?.amount + ''
                        : undefined;
                },
            },
        },
    ],
};

const CHILDREN_GOAL: Goal = {
    id: 'children',
    label: t('goal-indication-screen-children-title'),
    pages: [
        {
            pageId: 'children-amount-children',
            question: {
                label: (value) => t('goal-indication-screen-children-amount-title'), //'Hoeveel kinderen heb je?',
                type: 'quantity',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/children/amount-children',
                next: BASE_PATH_GOAL_INDICATION + '/children/amount-per-child',
            },
            value: {
                validate: (cents) => {
                    return null;
                },
                onChange: (value) => {
                    return {
                        goals: {
                            children: {
                                capitalGoal: {
                                    amountChildren: +value,
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.children?.capitalGoal?.amountChildren !== undefined
                        ? params.goals?.children?.capitalGoal?.amountChildren + ''
                        : undefined;
                },
                rules: {
                    min: 1,
                    max: 15,
                    step: 1,
                },
                sliderPreviewText: (amount) => {
                    if (amount === '1') return `${amount} kind`;
                    return `${amount} kinderen`;
                },
            },
        },
        {
            pageId: 'children-amount-per-child',
            question: {
                label: (value) => {
                    if (value?.goals?.children?.capitalGoal?.amountChildren === 1)
                        return t('goal-indication-screen-children-financial-goal-title-singular');
                    return t('goal-indication-screen-children-financial-goal-title-plural');
                }, //'Hoeveel heb je nodig per kind?',
                description: () => t('goal-indication-screen-children-financial-goal-description'), //'Dit bedrag vormt je financieel doel per kind. Aan de hand van dit bedrag wordt bepaald of je met je investering je doel bereikt. Het is mogelijk om dit later aan te passen.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/children/amount-per-child',
                next: BASE_PATH_GOAL_INDICATION + '/children/initial-deposit',
            },
            value: {
                placeholder: '€20.000',
                defaultValueOnEmpty: () => '2000000',
                validate: (cents) => {
                    return null;
                },
                onChange: (value) => {
                    return {
                        goals: {
                            children: {
                                capitalGoal: {
                                    amountPerChild: +value,
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.children?.capitalGoal?.amountPerChild !== undefined
                        ? params.goals.children.capitalGoal.amountPerChild + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'children-initial-deposit',
            question: {
                label: (value) => t('goal-indication-screen-children-initial-deposit-title'), //'Wat wil je eenmalig inleggen?',
                description: () => t('goal-indication-screen-general-initial-deposit-description'), //'Je eenmalige inleg vormt de basis van je investering. Hoe meer je inlegt, hoe meer kans je hebt op een hoog resultaat.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/children/initial-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/children/periodic-deposit',
            },
            value: {
                placeholder: '€1.000',
                defaultValueOnEmpty: () => '100000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            children: {
                                deposit: {
                                    initial: {
                                        amount: +value,
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.children?.deposit?.initial?.amount !== undefined
                        ? params.goals?.children?.deposit?.initial?.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'children-periodic-deposit',
            question: {
                label: (value) => t('goal-indication-screen-general-periodic-deposit-title'), //'Hoeveel wil je maandelijks inleggen?',
                description: () => t('goal-indication-screen-general-periodic-deposit-description'), //'Als je periodiek een bedrag inlegt, stijgt je rendement. Hoe meer je inlegt, hoe beter.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/children/periodic-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/children/capital-goal-date',
            },
            value: {
                placeholder: '€100',
                defaultValueOnEmpty: () => '10000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            children: {
                                deposit: {
                                    periodic: {
                                        amount: +value,
                                        interval: 'monthly',
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.children?.deposit?.periodic?.amount !== undefined
                        ? params.goals?.children?.deposit?.periodic?.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'children-capital-goal-date',
            question: {
                label: (value) => t('goal-indication-screen-general-capital-goal-inyears-title'), //'Over hoeveel jaar wil je het bij elkaar hebben?',
                //description: t('goal-indication-screen-general-capital-goal-inyears-description'), //'Dit aantal vormt de looptijd van je investering. Aan de hand van je looptijd wordt het resultaat van je investering berekend.',
                type: 'quantity',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/children/capital-goal-date',
                next: BASE_PATH_GOAL_INDICATION + '/general/expert-level',
            },
            value: {
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            children: {
                                capitalGoal: {
                                    inYears: +value,
                                },
                            },
                        },
                    };
                },
                rules: {
                    min: 1,
                    max: 50,
                    step: 1,
                },
                get: (params) => {
                    return params.goals?.children?.capitalGoal?.inYears
                        ? params.goals?.children?.capitalGoal?.inYears + ''
                        : undefined;
                },
                sliderPreviewText: (years) => `Over ${years} jaar`,
            },
        },
    ],
};

const TRAVEL_GOAL: Goal = {
    id: 'travel',
    label: t('goal-indication-screen-travel-title'),
    pages: [
        {
            pageId: 'travel-capital-goal-amount',
            question: {
                label: (value) => t('goal-indication-screen-travel-financial-goal-title'), //'Hoeveel heb je nodig voor de reis?',
                description: () => t('goal-indication-screen-travel-financial-goal-description'), //'Dit bedrag vormt je financieel doel. Aan de hand van dit bedrag wordt bepaald of je met je investering je doel bereikt. Het is mogelijk om dit later aan te passen.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/travel/capital-goal-amount',
                next: BASE_PATH_GOAL_INDICATION + '/travel/capital-goal-date',
            },
            value: {
                placeholder: '€15.000',
                defaultValueOnEmpty: () => '1500000',
                validate: (cents) => {
                    return null;
                },
                onChange: (value) => {
                    return {
                        goals: {
                            travel: {
                                capitalGoal: {
                                    amount: +value,
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.travel?.capitalGoal?.amount !== undefined
                        ? params.goals.travel.capitalGoal.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'travel-capital-goal-date',
            question: {
                label: (value) => t('goal-indication-screen-travel-financial-goal-in-years-title'), //'Over hoeveel jaar wil je op reis?',
                type: 'quantity',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/travel/capital-goal-date',
                next: BASE_PATH_GOAL_INDICATION + '/travel/initial-deposit',
            },
            value: {
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            travel: {
                                capitalGoal: {
                                    inYears: +value,
                                },
                            },
                        },
                    };
                },
                rules: {
                    min: 1,
                    max: 50,
                    step: 1,
                },
                get: (params) => {
                    return params.goals?.travel?.capitalGoal?.inYears
                        ? params.goals?.travel?.capitalGoal?.inYears + ''
                        : undefined;
                },
                sliderPreviewText: (years) => `Over ${years} jaar`,
            },
        },
        {
            pageId: 'travel-initial-deposit',
            question: {
                label: (value) => t('goal-indication-screen-travel-initial-deposit-title'), //'Wat wil je eenmalig inleggen?',
                description: () => t('goal-indication-screen-general-initial-deposit-description'), //'Je eenmalige inleg vormt de basis van je investering. Hoe meer je inlegt, hoe meer kans je hebt op een hoog resultaat.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/travel/initial-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/travel/periodic-deposit',
            },
            value: {
                placeholder: '€1.000',
                defaultValueOnEmpty: () => '100000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            travel: {
                                deposit: {
                                    initial: {
                                        amount: +value,
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.travel?.deposit?.initial?.amount !== undefined
                        ? params.goals?.travel?.deposit?.initial?.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'travel-periodic-deposit',
            question: {
                label: (value) => t('goal-indication-screen-general-periodic-deposit-title'), //'Hoeveel wil je maandelijks inleggen?',
                description: () => t('goal-indication-screen-general-periodic-deposit-description'), //'Als je periodiek een bedrag inlegt, stijgt je rendement. Hoe meer je inlegt, hoe beter.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/travel/periodic-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/general/expert-level',
            },
            value: {
                placeholder: '€500',
                defaultValueOnEmpty: () => '50000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            travel: {
                                deposit: {
                                    periodic: {
                                        amount: +value,
                                        interval: 'monthly',
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.travel?.deposit?.periodic?.amount !== undefined
                        ? params.goals?.travel?.deposit?.periodic?.amount + ''
                        : undefined;
                },
            },
        },
    ],
};

const CAPITAL_GROWTH_GOAL: Goal = {
    id: 'capital',
    label: t('goal-indication-screen-capital-growth-title'),
    pages: [
        {
            pageId: 'capital-initial-deposit',
            question: {
                label: (value) => t('goal-indication-screen-capital-initial-deposit-title'), //'Wat wil je eenmalig inleggen?',
                description: () => t('goal-indication-screen-general-initial-deposit-description'), //'Je eenmalige inleg vormt de basis van je investering. Hoe meer je inlegt, hoe meer kans je hebt op een hoog resultaat.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/capital/initial-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/capital/periodic-deposit',
            },
            value: {
                placeholder: '€2.500',
                defaultValueOnEmpty: () => '250000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            capital: {
                                deposit: {
                                    initial: {
                                        amount: +value,
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.capital?.deposit?.initial?.amount !== undefined
                        ? params.goals?.capital?.deposit?.initial?.amount + ''
                        : undefined;
                },
            },
        },
        {
            pageId: 'capital-periodic-deposit',
            question: {
                label: (value) => t('goal-indication-screen-general-periodic-deposit-title'), //'Hoeveel wil je maandelijks inleggen?',
                description: () => t('goal-indication-screen-general-periodic-deposit-description'), //'Als je periodiek een bedrag inlegt, stijgt je rendement. Hoe meer je inlegt, hoe beter.',
                type: 'money',
            },
            routes: {
                current: BASE_PATH_GOAL_INDICATION + '/capital/periodic-deposit',
                next: BASE_PATH_GOAL_INDICATION + '/general/expert-level',
            },
            value: {
                placeholder: '€200',
                defaultValueOnEmpty: () => '20000',
                validate: () => null,
                onChange: (value) => {
                    return {
                        goals: {
                            capital: {
                                deposit: {
                                    periodic: {
                                        amount: +value,
                                        interval: 'monthly',
                                    },
                                },
                            },
                        },
                    };
                },
                get: (params) => {
                    return params.goals?.capital?.deposit?.periodic?.amount !== undefined
                        ? params.goals?.capital?.deposit?.periodic?.amount + ''
                        : undefined;
                },
            },
        },
    ],
};

export const GOALS: Goal[] = [CAPITAL_GROWTH_GOAL, HOUSE_GOAL, TRAVEL_GOAL, CHILDREN_GOAL, PENSION_GOAL];

const buildContactDetailsPage = ( page : GoalPage, first: boolean ) => {
    const getNextPath = () => {
        if (first)
            return page.routes.current
        return page.routes.next;
    }

    const contactDetailsPage = {
        pageId: 'client-details',
        question: {
            label: (value) => t('forecast-screen-email-me-title'),
            description: () => t('goal-indication-screen-client-detail-sub-text'),
            type: 'contactDetails',
        },
        routes: {
            current: '/client-details-' + page.pageId,
            next: getNextPath(),
        },
        value: {
            validate: (value) => {
                return value;
            },
            onChange: () => {
                return {};
            },
            get: (params) => {
                return params.contactDetailsValid?  null : 'invalid';
            },
        },
    } as GoalPage
    return contactDetailsPage as GoalPage;
}

export const preFixDetailsPage = ( pages: GoalPage[]) => {
    const firstPage = pages[0];
    const contactDetailsPage = buildContactDetailsPage(firstPage, true);
    return [contactDetailsPage, ...pages];
}

export const postFixDetailsPage = ( pages: GoalPage[]) => {
    const lastPage = pages[pages.length - 1];
    const contactDetailsPage = buildContactDetailsPage(lastPage, false);
    lastPage.routes.next = contactDetailsPage.routes.current;
    return [...pages, contactDetailsPage];
}
